import type { CaptureOptions } from '../screenshot/entry.ts';
import type { Rect } from '../shared/types.ts';

export interface Capture {
  canvas: HTMLCanvasElement;
  /** Canvas pixels per CSS pixel. */
  scale: number;
  /** Window scroll position at capture time (document → capture coordinates). */
  scrollX: number;
  scrollY: number;
  /** Captured area in CSS pixels. */
  width: number;
  height: number;
  /** Whether element markers (document coordinates) can be mapped onto this image. */
  mappable: boolean;
}

export interface Annotation {
  kind: 'mark' | 'redact';
  /** In capture CSS pixels. */
  rect: Rect;
}

export const COLORS = { tanne: '#005437', sonne: '#FFF17A', sand: '#F5F1E9', redact: '#000000' };
const CAPTURE_TIMEOUT = 20_000;
const MAX_WIDTH = 2560;

let loader: Promise<void> | undefined;

function loadRenderer(src: string, nonce?: string): Promise<void> {
  if (window.__GSB_CAPTURE__) return Promise.resolve();
  loader ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.referrerPolicy = 'no-referrer';
    if (nonce) script.nonce = nonce;
    script.dataset.gsbIgnore = '';
    script.onload = () => (window.__GSB_CAPTURE__ ? resolve() : reject(new Error('Screenshot-Modul nicht initialisiert')));
    script.onerror = () => reject(new Error('Screenshot-Modul konnte nicht geladen werden'));
    document.head.append(script);
  }).catch((err) => {
    loader = undefined;
    throw err;
  });
  return loader;
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Zeitüberschreitung beim Screenshot')), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

/** Viewport rectangles of elements that must never be visible in a screenshot. */
function redactedRects(selectors: readonly string[]): Rect[] {
  const rects: Rect[] = [];
  for (const selector of selectors) {
    let nodes: NodeListOf<Element>;
    try {
      nodes = document.querySelectorAll(selector);
    } catch {
      continue;
    }
    for (const node of nodes) {
      const r = node.getBoundingClientRect();
      if (r.width === 0 || r.height === 0 || r.bottom < 0 || r.right < 0 || r.top > window.innerHeight || r.left > window.innerWidth) continue;
      rects.push({ x: r.left, y: r.top, width: r.width, height: r.height });
    }
  }
  return rects;
}

function fillRects(canvas: HTMLCanvasElement, rects: Rect[], scale: number): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = COLORS.redact;
  for (const r of rects) ctx.fillRect((r.x - 2) * scale, (r.y - 2) * scale, (r.width + 4) * scale, (r.height + 4) * scale);
}

/** Renders the visible page from the DOM – no permission prompt, but not pixel-perfect. */
export async function captureDom(options: { rendererUrl: string; nonce?: string; redactSelectors: readonly string[]; host: Element }): Promise<Capture> {
  await loadRenderer(options.rendererUrl, options.nonce);
  const scale = Math.min(window.devicePixelRatio || 1, 2);
  const scrollX = window.scrollX;
  const scrollY = window.scrollY;
  const redact = redactedRects(options.redactSelectors);
  const captureOptions: CaptureOptions = {
    scale,
    nonce: options.nonce,
    ignore: (el) => el === options.host || el.hasAttribute('data-gsb-ignore'),
  };
  const canvas = await withTimeout((window.__GSB_CAPTURE__ as NonNullable<Window['__GSB_CAPTURE__']>)(captureOptions), CAPTURE_TIMEOUT);
  fillRects(canvas, redact, scale);
  return { canvas, scale, scrollX, scrollY, width: window.innerWidth, height: window.innerHeight, mappable: true };
}

export function displayCaptureSupported(): boolean {
  return !!navigator.mediaDevices?.getDisplayMedia;
}

/** Real pixels via the Screen Capture API – needs the user's permission for every capture. */
export async function captureDisplay(redactSelectors: readonly string[]): Promise<Capture> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
  } as DisplayMediaStreamOptions);
  try {
    const track = stream.getVideoTracks()[0];
    if (!track) throw new Error('Keine Videospur');
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;
    await video.play();
    // Give the browser a moment to deliver a frame without the permission prompt.
    await new Promise((r) => setTimeout(r, 300));
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d')?.drawImage(video, 0, 0);
    const surface = (track.getSettings() as MediaTrackSettings & { displaySurface?: string }).displaySurface;
    const scale = canvas.width / window.innerWidth;
    // Only a capture of this tab lines up with page coordinates.
    const mappable = surface === 'browser' && Math.abs(canvas.height / scale - window.innerHeight) < 4;
    if (mappable) fillRects(canvas, redactedRects(redactSelectors), scale);
    return { canvas, scale, scrollX: window.scrollX, scrollY: window.scrollY, width: canvas.width / scale, height: canvas.height / scale, mappable };
  } finally {
    stream.getTracks().forEach((t) => t.stop());
  }
}

function strokeMark(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, hgt: number, unit: number): void {
  ctx.lineJoin = 'round';
  ctx.strokeStyle = COLORS.tanne;
  ctx.lineWidth = 6 * unit;
  ctx.strokeRect(x, y, w, hgt);
  ctx.strokeStyle = COLORS.sonne;
  ctx.lineWidth = 3 * unit;
  ctx.strokeRect(x, y, w, hgt);
}

function badge(ctx: CanvasRenderingContext2D, x: number, y: number, label: string, unit: number): void {
  const r = 12 * unit;
  const cx = Math.max(r, x);
  const cy = Math.max(r, y);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.tanne;
  ctx.fill();
  ctx.lineWidth = 2 * unit;
  ctx.strokeStyle = COLORS.sonne;
  ctx.stroke();
  ctx.fillStyle = COLORS.sonne;
  ctx.font = `bold ${14 * unit}px "PT Sans", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, cx, cy + unit);
}

export function drawAnnotation(ctx: CanvasRenderingContext2D, a: Annotation, scale: number): void {
  const { x, y, width, height } = a.rect;
  if (a.kind === 'redact') {
    ctx.fillStyle = COLORS.redact;
    ctx.fillRect(x * scale, y * scale, width * scale, height * scale);
  } else {
    strokeMark(ctx, x * scale, y * scale, width * scale, height * scale, scale);
  }
}

/** Capture + numbered element markers + user annotations, as a new canvas. */
export function compose(capture: Capture, elementRects: Rect[], annotations: Annotation[]): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = capture.canvas.width;
  canvas.height = capture.canvas.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return capture.canvas;
  ctx.drawImage(capture.canvas, 0, 0);
  const s = capture.scale;

  for (const a of annotations) drawAnnotation(ctx, a, s);

  if (capture.mappable) {
    elementRects.forEach((r, i) => {
      const x = (r.x - capture.scrollX) * s;
      const y = (r.y - capture.scrollY) * s;
      const w = r.width * s;
      const hgt = r.height * s;
      if (x > canvas.width || y > canvas.height || x + w < 0 || y + hgt < 0) return;
      strokeMark(ctx, x - 3 * s, y - 3 * s, w + 6 * s, hgt + 6 * s, s);
      badge(ctx, x - 3 * s, y - 3 * s, String(i + 1), s);
    });
  }
  return canvas;
}

/** PNG for crisp UI text; falls back to JPEG for large/photo-heavy captures. */
export function encode(source: HTMLCanvasElement): string {
  let canvas = source;
  if (source.width > MAX_WIDTH) {
    const factor = MAX_WIDTH / source.width;
    canvas = document.createElement('canvas');
    canvas.width = MAX_WIDTH;
    canvas.height = Math.round(source.height * factor);
    canvas.getContext('2d')?.drawImage(source, 0, 0, canvas.width, canvas.height);
  }
  const png = canvas.toDataURL('image/png');
  if (png.length < 1_500_000) return png;
  return canvas.toDataURL('image/jpeg', 0.85);
}
